import type React from "react";
import { useNavigate } from "react-router";
import { ScrollArea, ScrollBar } from "@acme/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardContent } from "@acme/ui/card";
import {
	ArrowRightIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon,
} from "lucide-react";

// Define the assignments interface
export interface Assignment {
	id: string;
	name: string;
	email: string;
	status: "completed" | "in progress" | "expired";
}

const assignments = [
	{ id: "a1", name: "Alice", email: "alice@example.com", status: "completed" },
	{ id: "a2", name: "Bob", email: "bob@example.com", status: "in progress" },
	{
		id: "a3",
		name: "Charlie",
		email: "charlie@example.com",
		status: "expired",
	},
];

// Map statuses to their corresponding icon/logo
const STATUS_ICONS: Record<Assignment["status"], React.ReactNode> = {
	completed: <CheckCircleIcon className="h-6 w-6 text-green-500" />,
	"in progress": <ClockIcon className="h-6 w-6 text-yellow-500" />,
	expired: <XCircleIcon className="h-6 w-6 text-red-500" />,
};

export function Assignments() {
	const navigate = useNavigate();

	return (
		<div className="p-4">
			<h1 className="text-2xl font-bold mb-4">Assignments</h1>
			<ScrollArea className="pb-4">
				<div className="flex gap-4 w-max p-4">
					{assignments.map((assignment) => (
						<Card
							key={assignment.id}
							className="min-w-[250px] cursor-pointer hover:shadow-lg transition-shadow"
							onClick={() =>
								navigate(`/dashboard/assignments/${assignment.id}`)
							}
						>
							<CardHeader className="p-4">
								<div className="flex items-center gap-2 mb-2">
									{STATUS_ICONS[assignment.status]}
									<CardTitle className="text-xl font-semibold">
										{assignment.name}
									</CardTitle>
								</div>
							</CardHeader>
							<CardContent className="px-4 pb-4">
								<p className="text-sm text-gray-600">{assignment.email}</p>
								<p className="text-xs text-gray-500 capitalize">
									Status: {assignment.status}
								</p>
							</CardContent>
						</Card>
					))}
				</div>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>
			<button
				type="button"
				onClick={() => navigate("/assignments")}
				className="flex items-center gap-2 text-primary font-semibold"
			>
				See All
				<ArrowRightIcon className="h-4 w-4" />
			</button>
		</div>
	);
}
